import moment from 'moment';
import get from 'lodash/get';
import {
  CursorAccess,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  MIS_COMPLIANCE_INDEX,
  QueryType,
} from '../../db/constants';
import {
  BusinessLevels,
  IRNComplianceSummary,
  IRNSummaryDetails,
  MISReport,
  MISReportSummaryResponse,
} from '../../common/types';
import { getCursor } from '../../db/db';
import { appConfig } from '../../config/app-config';
import { callApi } from '../../common/api';
import { API_END_POINTS } from '../../config/api-endpoints';
import { createOrUpdateComplianceSummary } from '../../db/compliance-db';

const getComplianceSummaryFromDB = async (
  orgId: string,
  invoiceStartDate: moment.Moment,
  invoiceEndDate: moment.Moment
): Promise<IRNComplianceSummary[] | undefined> => {
  try {
    let cursor = await getCursor(
      appConfig.IDB.STORE.MIS_COMPLIANCE,
      MIS_COMPLIANCE_INDEX.ORG_ID_INDEX.NAME,
      orgId,
      CursorAccess.READ_ONLY,
      QueryType.ONLY
    );

    while (cursor) {
      if (
        moment(cursor.value.invoiceStartDate).format(DATE_FORMAT) ===
          invoiceStartDate.format(DATE_FORMAT) &&
        moment(cursor.value.invoiceEndDate).format(DATE_FORMAT) ===
          invoiceEndDate.format(DATE_FORMAT)
      ) {
        return cursor.value.summary as IRNComplianceSummary[];
      }
      cursor = await cursor.continue();
    }
  } catch (e) {
    // console.log('Failed to get compliance summary from db', e);
  }
};

const generateSummary = (
  summaries: MISReport[],
  gstin: string,
  gstinOrgName: string
) => {
  let complianceSummaries: IRNComplianceSummary[] = [];
  for (const summary of summaries) {
    const complianceSummary: IRNComplianceSummary = {
      gstinOrgName,
      gstin,
      orgId: summary.Id,
      irnSummaryDetails: {
        totalPendingIrnGeneration: get(
          summary,
          'ReportsData.NotGenerated.Count',
          0
        ),
        totalNoOfInvoices: get(summary, 'ReportsData.Total.Count', 0),
        totalIrnGenerated: get(summary, 'ReportsData.Generated.Count', 0),
        totalIrnFailed: get(summary, 'ReportsData.Failed.Count', 0),
        totalIrnCancelled: get(summary, 'ReportsData.Cancelled.Count', 0),
      },
    };
    if (summary.Level === BusinessLevels.BRANCH) {
      complianceSummary.branchOrgName = summary.Name;
    }
    complianceSummaries = complianceSummaries.concat(complianceSummary);
  }
  return complianceSummaries;
};

const generateGstinLevelSummary = (
  branchComplianceSummaries: IRNComplianceSummary[],
  stateComplianceSummary: IRNComplianceSummary
): IRNComplianceSummary => {
  const cumulativeBranchSummary: IRNSummaryDetails = {
    totalIrnCancelled: 0,
    totalIrnFailed: 0,
    totalIrnGenerated: 0,
    totalPendingIrnGeneration: 0,
    totalNoOfInvoices: 0,
  };
  for (const branchComplianceSummary of branchComplianceSummaries) {
    const branchSummary = branchComplianceSummary.irnSummaryDetails;

    cumulativeBranchSummary.totalNoOfInvoices +=
      branchSummary.totalNoOfInvoices;
    cumulativeBranchSummary.totalPendingIrnGeneration +=
      branchSummary.totalPendingIrnGeneration;
    cumulativeBranchSummary.totalIrnGenerated +=
      branchSummary.totalIrnGenerated;
    cumulativeBranchSummary.totalIrnFailed += branchSummary.totalIrnFailed;
    cumulativeBranchSummary.totalIrnCancelled +=
      branchSummary.totalIrnCancelled;
  }

  const stateSummary = stateComplianceSummary.irnSummaryDetails;

  const gstinLevelSummary: IRNSummaryDetails = {
    totalIrnCancelled:
      stateSummary.totalIrnCancelled -
      cumulativeBranchSummary.totalIrnCancelled,
    totalIrnFailed:
      stateSummary.totalIrnFailed - cumulativeBranchSummary.totalIrnFailed,
    totalIrnGenerated:
      stateSummary.totalIrnGenerated -
      cumulativeBranchSummary.totalIrnGenerated,
    totalPendingIrnGeneration:
      stateSummary.totalPendingIrnGeneration -
      cumulativeBranchSummary.totalPendingIrnGeneration,
    totalNoOfInvoices:
      stateSummary.totalNoOfInvoices -
      cumulativeBranchSummary.totalNoOfInvoices,
  };

  return {
    ...stateComplianceSummary,
    irnSummaryDetails: gstinLevelSummary,
  };
};

const generateAllComplianceSummary = (
  complianceData: MISReportSummaryResponse,
  panOrgId: string,
  gstinOrgIdGstinOrgNameMap: Map<string, string>
) => {
  let complianceSummaries: IRNComplianceSummary[] = [];

  // GSTIN + Branches data
  const stateLevelSummary = complianceData.MisReport.filter(
    (misSummary: MISReport) => misSummary.ParentOrgUnitId === panOrgId
  );

  stateLevelSummary.forEach((stateSummary: MISReport) => {
    let branchComplianceSummaries;
    let gstinComplianceSummary;

    const gstin = stateSummary.Name;
    const gstinOrgName = gstinOrgIdGstinOrgNameMap.get(stateSummary.Id) || '';

    const stateComplianceSummary = generateSummary(
      [stateSummary],
      gstin,
      gstinOrgName
    )[0];

    const branchLevelSummaries = complianceData.MisReport.filter(
      (misSummary: MISReport) => misSummary.ParentOrgUnitId === stateSummary.Id
    );
    if (branchLevelSummaries.length !== 0) {
      branchComplianceSummaries = generateSummary(
        branchLevelSummaries,
        gstin,
        gstinOrgName
      );
      complianceSummaries = complianceSummaries.concat(
        branchComplianceSummaries
      );

      // Only GSTIN level data
      gstinComplianceSummary = generateGstinLevelSummary(
        branchComplianceSummaries,
        stateComplianceSummary
      );
      complianceSummaries = complianceSummaries.concat(gstinComplianceSummary);
    } else {
      complianceSummaries = complianceSummaries.concat(stateComplianceSummary);
    }
  });
  return complianceSummaries;
};

const getComplianceSummaryFromApi = async (
  invoiceStartDate: string,
  invoiceEndDate: string,
  panOrgId: string,
  panNumber: string,
  gstinOrgIdGstinOrgNameMap: Map<string, string>,
  businessId: string,
) => {
  const filter = {
    invoiceStartDate,
    invoiceEndDate,
  };
  const headers = {
    PAN: panNumber,
    'node-id': businessId,
    'node-type': 'PAN',
  };

  const response = await callApi({
    method: 'POST',
    url: API_END_POINTS.EINVOICE.SUMMARY,
    data: { filter },
    headers,
  });

  const complianceSummaryData = response.data as MISReportSummaryResponse;

  const complianceSummary = generateAllComplianceSummary(
    complianceSummaryData,
    panOrgId,
    gstinOrgIdGstinOrgNameMap
  );

  createOrUpdateComplianceSummary(
    panOrgId,
    moment(invoiceStartDate, DATE_TIME_FORMAT),
    moment(invoiceEndDate, DATE_TIME_FORMAT),
    complianceSummary
  );
  return complianceSummary;
};

export const getComplianceSummary = async (
  invoiceStartDate: string,
  invoiceEndDate: string,
  panOrgId: string,
  panNumber: string,
  gstinOrgIdGstinOrgNameMap: Map<string, string>,
  businessId: string,
) => {
  const responseFromDb = await getComplianceSummaryFromDB(
    panOrgId,
    moment(invoiceStartDate, DATE_TIME_FORMAT),
    moment(invoiceEndDate, DATE_TIME_FORMAT)
  );

  const responseFromApi = getComplianceSummaryFromApi(
    invoiceStartDate,
    invoiceEndDate,
    panOrgId,
    panNumber,
    gstinOrgIdGstinOrgNameMap,
    businessId,
  );

  if (responseFromDb) {
    // console.log('From db');
    return responseFromDb;
  }
  // console.log('From api');
  const response = await responseFromApi;
  return response;
};
